import { useEffect, useRef, useState } from "react";
import { Box, IconButton, Link, Tooltip } from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Link as RouterLink } from "react-router-dom";
import { useNotify } from "react-admin";
import { copyText } from "./clipboard";
import { formatDateTime, valueText } from "./format";

export interface DataValueProps {
  value: unknown;
  label?: string;
  copy?: boolean;
  date?: boolean;
  to?: string;
  href?: string;
  maxWidth?: number;
}

/** One line on screen; complete, unchanged API value when copied. */
export function DataValue({
  value,
  label = "value",
  copy = false,
  date = false,
  to,
  href,
  maxWidth = 320,
}: DataValueProps) {
  const raw = valueText(value);
  const empty = value === null || value === undefined || value === "";
  const text = date ? formatDateTime(value) : raw;
  const [feedback, setFeedback] = useState<
    "idle" | "pending" | "copied" | "failed"
  >("idle");
  const latest = useRef({ raw, generation: 0, mounted: true });
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();
  const notify = useNotify();

  useEffect(() => {
    latest.current = {
      raw,
      generation: latest.current.generation + 1,
      mounted: true,
    };
    setFeedback("idle");
    return () => {
      latest.current.mounted = false;
      latest.current.generation += 1;
      if (resetTimer.current !== undefined) clearTimeout(resetTimer.current);
    };
  }, [raw]);

  const handleCopy = async () => {
    if (feedback === "pending") return;
    if (resetTimer.current !== undefined) clearTimeout(resetTimer.current);
    const generation = ++latest.current.generation;
    setFeedback("pending");
    const copied = await copyText(raw).catch(() => false);
    if (
      !latest.current.mounted ||
      generation !== latest.current.generation ||
      latest.current.raw !== raw
    )
      return;
    setFeedback(copied ? "copied" : "failed");
    notify(
      copied
        ? `${label} copied`
        : "Copy failed. Select the value and copy it manually.",
      {
        type: copied ? "success" : "warning",
        autoHideDuration: copied ? 1800 : 4000,
      },
    );
    resetTimer.current = setTimeout(
      () => {
        if (latest.current.mounted && generation === latest.current.generation)
          setFeedback("idle");
      },
      copied ? 1800 : 4000,
    );
  };

  const valueStyle = {
    display: "block",
    minWidth: 0,
    maxWidth,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  } as const;
  const content = date ? (
    <Box component="time" dateTime={empty ? undefined : raw} sx={valueStyle}>
      {text}
    </Box>
  ) : to && !empty ? (
    <Link component={RouterLink} to={to} sx={valueStyle}>
      {text}
    </Link>
  ) : href && !empty ? (
    <Link href={href} target="_blank" rel="noopener noreferrer" sx={valueStyle}>
      {text}
    </Link>
  ) : (
    <Box component="span" sx={valueStyle}>
      {text}
    </Box>
  );

  return (
    <Box
      component="span"
      title={empty ? undefined : raw}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "middle",
        gap: 0.5,
        minWidth: 0,
        maxWidth: "100%",
        whiteSpace: "nowrap",
      }}
    >
      {content}
      {copy && !empty && (
        <Tooltip
          title={
            feedback === "copied"
              ? "Copied"
              : feedback === "failed"
                ? "Copy failed — select the value to copy"
                : `Copy ${label}`
          }
        >
          <IconButton
            type="button"
            size="small"
            aria-label={`Copy ${label}`}
            disabled={feedback === "pending"}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              void handleCopy();
            }}
            sx={{
              p: 0.375,
              flexShrink: 0,
              color:
                feedback === "copied"
                  ? "success.main"
                  : feedback === "failed"
                    ? "warning.main"
                    : "text.secondary",
              "&:hover": { color: "primary.light" },
            }}
          >
            {feedback === "copied" ? (
              <CheckIcon sx={{ fontSize: 16 }} />
            ) : feedback === "failed" ? (
              <ErrorOutlineIcon sx={{ fontSize: 16 }} />
            ) : (
              <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
